<template>
  <v-row no-gutters>
    <v-col cols="auto" v-if="isFetched">
      <v-row align="center" class="pb-3" no-gutters>
        <v-col cols="7">
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-2">
              <v-icon color="rgba(0,0,0,0.7)">
                mdi-calendar
              </v-icon>
            </v-col>
            <v-col cols="6" class="mr-4">
              <data-input v-model="dateType" v-bind="dateTypeInput" />
            </v-col>
            <v-col cols="4">
              <data-input v-model="selectType" v-bind="selectTypeInput" />
            </v-col>
          </v-row>
        </v-col>
        <v-spacer />
        <v-col cols="3">
          <data-input v-model="searchType" v-bind="searchTypeInput" />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="items"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 20, 50],
            }"
            :options.sync="options"
            :server-items-length="totalItems"
            style="border: 1px solid #DDD"
          >
            <template #item.icon="{ item }">
              <v-icon small :color="setIconColor(item.type)">
                mdi-alert-circle
              </v-icon>
            </template>
            <template #item.type="{ item }">
              {{ $i18n.t(`admin.eventsLog_table.items.${item.type}`) }}
            </template>
            <template #item.details="{ item }">
              <v-tooltip color="font" right>
                <template #activator="{ on }">
                  <v-icon v-on="on" small @click="getEventLogDetails(item.id)">
                    mdi-clipboard-search
                  </v-icon>
                </template>
                <span>{{ $i18n.t('sidebar.eventslog.checkDetails') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-large-dialog
        :is-visible="isVisible"
        :title="$i18n.t('dialog.title.eventDetails')"
        :has-agree="false"
        disagree-text="dialog.close"
        header-shadow
        @modalClosed="onClose"
      >
        <template #body>
          <v-card-text v-if="isLoaded" class="text-left pa-0">
            {{ eventDetails }}
          </v-card-text>
          <main-loader v-else />
        </template>
      </main-large-dialog>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'EventsLogTab',
  components: {
    DataInput,
    MainLargeDialog,
    MainLoader,
  },
  data: () => ({
    isVisible: false,
    isFetched: false,
    isLoaded: false,
    dateType: [],
    selectType: null,
    searchType: '',
    eventDetails: '',
    items: [],
    totalItems: 0,
    options: {},
  }),
  computed: {
    dateTypeInput() {
      return {
        dataType: { name: 'daterange' },
        label: this.$i18n.t('sidebar.eventslog.dateLabel'),
        localeDateFormat: 'YYYY-MM-DD',
        returnType: 'date',
        hideDetails: true,
        clearable: true,
      };
    },
    selectTypeInput() {
      return {
        dataType: { name: 'select' },
        items: this.selectTypes,
        label: this.$i18n.t('sidebar.eventslog.typeLabel'),
        hideDetails: true,
        clearable: true,
      };
    },
    selectTypes() {
      return [
        {
          value: 'EVENT',
          text: this.$i18n.t('admin.eventsLog_table.items.EVENT'),
        },
        {
          value: 'ERROR',
          text: this.$i18n.t('admin.eventsLog_table.items.ERROR'),
        },
        {
          value: 'INFO',
          text: this.$i18n.t('admin.eventsLog_table.items.INFO'),
        },
      ];
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        label: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
    headers() {
      const headers = [
        {
          name: 'icon',
          width: 50,
          sortable: false,
        },
        {
          name: 'id',
          width: 100,
          sortable: true,
        },
        {
          name: 'type',
          width: 150,
          sortable: true,
        },
        {
          name: 'name',
          width: 250,
          sortable: true,
        },
        {
          name: 'date',
          width: 150,
          sortable: true,
        },
        {
          name: 'description',
          width: 550,
          sortable: true,
        },
        {
          name: 'details',
          width: 50,
          sortable: false,
        },
      ];
      return headers.map(header => {
        return {
          text: this.$i18n.t(`admin.eventsLog_table.headers.${header.name}`),
          value: header.name,
          align: 'center',
          width: header.width,
          class: 'px-3',
          cellClass: 'py-2 px-3',
        };
      });
    },
    searchItemsFilter() {
      return {
        '!OR': [
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.id': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.name': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.description': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.details': { value: this.searchType ? this.searchType : '' },
            },
          },
        ],
      };
    },
    isEventSearching() {
      return this.dateType?.length === 2 || !!this.selectType || !!this.searchType;
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.dateType, vm.selectType, vm.searchType, vm.options],
      () => {
        this.init();
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  methods: {
    getEventLog: call('admin/getEventLog'),
    getEventsLog: call('admin/getEventsLog'),
    async init() {
      const { page = 1, itemsPerPage = 10, sortBy = [], sortDesc = [] } = this.options;
      const payload = {
        features_filter: this.isEventSearching
          ? {
              '!AND': [
                ...(this.searchType.length > 0 ? [this.searchItemsFilter] : []),
                ...(this.dateType?.length === 2
                  ? [
                      {
                        $GREATER_OR_EQUAL: {
                          'events_event_logs.date': { value: this.dateType[0] },
                        },
                      },
                      {
                        $LESS_OR_EQUAL: {
                          'events_event_logs.date': { value: this.dateType[1] },
                        },
                      },
                    ]
                  : []),
                ...(this.selectType
                  ? [
                      {
                        $EQUAL: {
                          'events_event_logs.type': { value: this.selectType },
                        },
                      },
                    ]
                  : []),
              ],
            }
          : undefined,
        sorting: [
          {
            attribute_name: `events_event_logs.${sortBy.length === 1 ? sortBy[0] : 'id'}`,
            direction: sortDesc[0] ? 'asc' : 'desc',
          },
        ],
        params: {
          limit: itemsPerPage,
          page: page - 1,
          with_total_count: true,
        },
      };
      try {
        const r = await this.getEventsLog(payload);
        this.items = await r.features.features.map(item => {
          return {
            ...item.properties,
            id: item.id,
            date: item.properties.date.slice(0, 10),
          };
        });
        this.totalItems = r.total_features_count;
        this.isFetched = true;
      } catch (error) {
        console.log('error:', error);
      }
    },
    async getEventLogDetails(id) {
      this.isVisible = true;
      try {
        const r = await this.getEventLog(id);
        this.eventDetails = r.details;
        this.isLoaded = true;
      } catch (error) {
        console.log('error:', error);
      }
    },
    setIconColor(type) {
      if (type === 'ERROR') {
        return 'red';
      } else if (type === 'INFO') {
        return 'blue';
      } else if (type === 'EVENT') {
        return 'orange';
      } else {
        return 'grey';
      }
    },
    onClose() {
      this.isVisible = false;
      this.isLoaded = false;
      this.eventDetails = '';
    },
  },
};
</script>
